import React, { useState } from 'react';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowPathIcon, 
  TrophyIcon, 
  CheckIcon
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';

const Summary = ({ questions, onRestart, onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);

  const handleComplete = async () => {
    setIsLoading(true);
    setShowSlowConnectionMessage(true);  

    try {
      await onComplete();
    } finally {
      setIsLoading(false);
    }
  };

  const correctAnswers = questions.filter(question => question.result === 'Correct').length;
  const percentage = Math.round((correctAnswers / questions.length) * 100);
  
  // Determine performance level
  const getPerformanceLevel = () => {
    if (percentage >= 90) return { text: 'Ausgezeichnet!', color: 'text-success' };
    if (percentage >= 75) return { text: 'Sehr gut!', color: 'text-primary' };
    if (percentage >= 50) return { text: 'Gut gemacht!', color: 'text-warning' };
    return { text: 'Weiter üben!', color: 'text-error' };
  };
  
  const performance = getPerformanceLevel();

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="card bg-base-200 shadow-lg max-w-md w-full p-6">
          <div className="flex flex-col items-center gap-4">
            <div className="loading loading-spinner loading-lg text-primary"></div>
            <p className="text-base font-medium">Quiz wird gespeichert...</p>
            {showSlowConnectionMessage && (
              <div className="text-sm text-base-content/70 text-center animate-fade-in">
                <p>Dein Fortschritt wird gespeichert.</p>
                <p>Du wirst in wenigen Sekunden weitergeleitet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="p-4 sm:p-6 space-y-6">
        {/* Header Section */}
        <div className="text-center space-y-2">
          <TrophyIcon className={`w-16 h-16 mx-auto ${performance.color}`} />
          <h2 className={`text-2xl sm:text-3xl font-bold ${performance.color}`}>
            {performance.text}
          </h2>
          <p className="text-base-content/70">
            Du hast das Quiz abgeschlossen
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="card bg-base-200">
            <div className="card-body p-4 text-center">
              <div className="text-4xl font-bold text-primary">
                {correctAnswers}/{questions.length}
              </div>
              <div className="text-sm text-base-content/70">
                Richtige Antworten
              </div>
            </div>
          </div>
          <div className="card bg-base-200">
            <div className="card-body p-4 text-center">
              <div className="text-4xl font-bold text-primary">
                {percentage}%
              </div>
              <div className="text-sm text-base-content/70">
                Erfolgsquote
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="card bg-base-200">
          <div className="card-body p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                className="btn btn-primary gap-2 normal-case"
                onClick={onRestart}
              >
                <ArrowPathIcon className="w-5 h-5" />
                Quiz erneut starten
              </button>
              <button
                className="btn btn-success gap-2 normal-case"
                onClick={handleComplete}
              >
                <CheckIcon className="w-5 h-5" />
                Quiz abschließen
              </button>
            </div>
          </div>
        </div>

        {/* Questions Review */}
        <div className="card bg-base-200">
          <div className="card-body p-4">
            <h3 className="card-title text-lg mb-4">Fragen-Übersicht</h3>
            <div className="space-y-4">
              {questions.map((question, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-lg bg-base-100 border ${
                    question.result === 'Correct' 
                      ? 'border-success/20' 
                      : 'border-error/20'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {question.result === 'Correct' ? (
                      <CheckCircleIcon className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                    ) : (
                      <XCircleIcon className="w-6 h-6 text-error flex-shrink-0 mt-1" />
                    )}
                    <div className="flex-1 prose prose-sm max-w-none">
                      <ReactMarkdown>
                        {question.question_text}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Summary;